import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge, Button, Col, Form, Row,
} from 'react-bootstrap';
import Table from 'components/Table';
import TextField from 'components/Form/TextField';
import { CellProps } from 'react-table';
import { DTable } from 'types/table';
import iconDownload from 'assets/img/icons/download.svg';
import { formatPhoneNumber } from 'utils/formats';
import { useNavigate } from 'react-router-dom';
import { IoIosAddCircleOutline } from 'react-icons/io';
import {
  MODAL_ACTIVE_ADMIN, MODAL_ACTIVE_USER, MODAL_ADD_USER, MODAL_DISABLED_ADMIN, MODAL_DISABLED_USER,
} from 'constants/utils';
import _ from 'lodash';
import classnames from 'classnames';
import { hasInvestorUser, hasSsoUserId, isAdminInvestor } from 'utils/investorContactGuards';

function investorNavState( row: any ) {
  return {
    fullName: `${row.firstName} ${row.lastName}`,
    credentialState: row.credentialState,
    ssoUserId: row.ssoUserId,
    roles: row.roles,
    userId: row.userId,
    extraData: { email: row.email },
  };
}

function Investors( {
  users, fetchData, isLoading, type, onOpenCreateUserModal,
  openCreateUserAdminModal,
  downloadDocument,
}:{
  onOpenCreateUserModal: ( params:any )=>void
  openCreateUserAdminModal: ( params:any )=>void
  type?:string;
  isLoading?:boolean;
  users:DTable;
  downloadDocument: ( params:any )=>void ;
  fetchData: ( params:any, pageSize:number )=>void ; } ) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState( '' );

  const debounceSearch = useCallback( _.debounce( ( fn ) => fn(), 500 ), [] );
  const getContactRoleLabel = ( row:any ) => {
    if ( row.contactoPrincipal ) return 'Contacto principal';
    if ( row.contactoSecundario ) return 'Contacto secundario';
    if ( row.representanteLegal ) return 'Representante legal';
    if ( row.intermediario ) return 'Intermediario';
    if ( row.abogado ) return 'Abogado';
    return '-';
  };

  const headers = [
    {
      Header: t( 'admin.investor.header1' ),
      headerClassName: 'text--left',
      accessor: 'firstName',
      className: 'text--bold',
      Cell: ( { cell }: CellProps<any> ) => (
        <span
          className="text--bold cursor-pointer"
          onClick={() => {
            const id = cell.row.original.id || cell.row.original.contacId;
            navigate(
              `/admin/investors/${id}/accounts/user`,
              { state: investorNavState( cell.row.original ) },
            );
          }}
        >
          {`${cell.value} ${cell.row.original.lastName}`}
        </span>
      ),
    },
    {
      Header: t( 'admin.investor.header2' ),
      headerClassName: 'text--left',
      accessor: 'email',
      customWidth: 300,
    },
    {
      Header: t( 'admin.investor.header3' ),
      headerClassName: 'text--left',
      accessor: 'mobile',
      customWidth: 150,
      Cell: ( { cell }: CellProps<any> ) => <span className="text--bold">{formatPhoneNumber( cell.value )}</span>,
    },
    {
      Header: t( 'admin.investor.header4' ),
      headerClassName: 'text--left',
      accessor: 'dninie',
      customWidth: 150,
    },
  ];
  if ( type !== 'pro' ) {
    if ( type === 'inv' ) {
      headers.push( {
        headerClassName: 'text--left',
        Header: 'ROL',
        accessor: 'contactRole',
        customWidth: 180,
        Cell: ( { cell }: CellProps<any> ) => <span className="text--bold">{getContactRoleLabel( cell.row.original )}</span>,
      } );
    }

    headers.push( {
      headerClassName: 'text--left',
      Header: t( 'admin.investor.header5' ),
      accessor: 'estado',
      customWidth: 100,
      Cell: ( { cell }: CellProps<any> ) => {
        const row = cell.row.original;
        const canToggleBlock = hasInvestorUser( row ) && ( cell.value === 0 || cell.value === 1 );
        return (
          <Button
            variant="link"
            className={classnames( 'p-0', {
              'cursor-no-pointer': !canToggleBlock,
              'cursor-pointer': canToggleBlock,
            } )}
            onClick={() => {
              if ( canToggleBlock ) {
                onOpenCreateUserModal( {
                  id: row.id,
                  fullName: `${row.firstName} ${row.lastName}`,
                  action: cell.value === 0 ? MODAL_ACTIVE_USER : MODAL_DISABLED_USER,
                } );
              }
            }}
          >
            <span
              className="text--bold"
            >
              <Form.Check
                type="switch"
                disabled={!hasInvestorUser( row )}
                defaultChecked={hasInvestorUser( row ) && cell.value === 0}
              />
            </span>
          </Button>
        );
      },
    } );
    // @ts-ignore
    headers.push( {
      Header: t( 'admin.investor.header6' ),
      headerClassName: 'text--left',
      accessor: 'credentialState',
      customWidth: 150,
      Cell: ( { cell }: CellProps<any> ) => {
        const row = cell.row.original;
        const credentialValue = Number( cell.value );
        const contactId = row.id || row.contacId;
        const investorReady = hasInvestorUser( row );

        let bg = 'primary';
        let text = 'AGREGAR';
        if ( credentialValue === 2 ) {
          bg = 'danger';
          text = 'DESCARTADO';
        } else if ( investorReady ) {
          bg = 'light';
          text = 'CREADO';
        }

        const canOpenAddUser = credentialValue === 2
          || credentialValue === 0
          || ( credentialValue === 1 && !hasSsoUserId( row ) );

        return (
          <Badge
            bg={bg}
            className={investorReady ? '' : 'cursor-pointer'}
            onClick={() => {
              if ( canOpenAddUser ) {
                onOpenCreateUserModal( {
                  id: contactId,
                  fullName: `${row.firstName} ${row.lastName}`,
                  action: MODAL_ADD_USER,
                } );
              }
            }}
          >
            {text === 'AGREGAR' && (
            <IoIosAddCircleOutline
              size={20}
              style={{ marginRight: '5px' }}
            />
            )}
            {text}
          </Badge>
        );
      },
    } );

    headers.push( {
      headerClassName: 'text--left',
      Header: t( 'admin.investor.header51' ),
      accessor: 'roles',
      customWidth: 100,
      Cell: ( { cell }: CellProps<any> ) => {
        const row = cell.row.original;
        const adminOn = isAdminInvestor( row );
        const canToggleAdmin = hasInvestorUser( row );
        return (
          <Button
            variant="link"
            className={classnames( 'p-0', {
              'cursor-no-pointer': !canToggleAdmin,
              'cursor-pointer': canToggleAdmin,
            } )}
            onClick={() => {
              if ( canToggleAdmin ) {
                openCreateUserAdminModal( {
                  id: row.id,
                  userId: row.userId,
                  fullName: `${row.firstName} ${row.lastName}`,
                  action: adminOn ? MODAL_DISABLED_ADMIN : MODAL_ACTIVE_ADMIN,
                } );
              }
            }}
          >
            <span
              className="text--bold"
            >
              <Form.Check
                type="switch"
                className="switch-admin"
                disabled={!canToggleAdmin}
                defaultChecked={adminOn}
              />
            </span>
          </Button>
        );
      },
    } );
  }
  let defaultSortBy = [{ id: 'firstName', desc: true }];
  if ( type === 'inv' ) {
    defaultSortBy = [{ id: 'firstName', desc: true }];
  }
  if ( type === 'pro' ) {
    defaultSortBy = [{ id: 'firstName', desc: true }];
  }

  return (
    <Table
      columns={[...headers]}
      isLoading={isLoading}
      data={users.data}
      total={users.totalItems}
      pageCount={users.pageCount}
      fetchData={fetchData}
      defaultSortBy={defaultSortBy}
      header={( filters, setFilters ) => (
        <Row className="align-items-center">
          <Col lg={9} sm={8} xs={6}>
            <Button
              size="sm"
              className="pt-2 pb-2 ps-4 pe-4 text--normal btn--rounded d-flex align-items-center"
              onClick={() => downloadDocument( { download: true } )}
            >
              <div>
                <img src={iconDownload} alt="" className="icon--img" />
                {t( 'admin.common.download' )}
              </div>
            </Button>
          </Col>
          <Col lg={3} sm={4} xs={6}>
            <TextField
              value={criteria}
              showErrors={false}
              placeholder="search"
              appendIcon="icon-search text-primary"
              onChange={( value ) => {
                setCriteria( value );
                debounceSearch( () => setFilters( ['firstName', 'lastName'], value ) );
              }}
              classNames={{ formGroup: 'input-filters' }}
            />
          </Col>
        </Row>
      )}
    />
  );
}

export default Investors;
